import React, { useState, useEffect } from 'react'
import { Users, Video, Calendar, Clock, DollarSign, Star, Play, Pause, Mic, MicOff, Video as VideoIcon, VideoOff, Share2, MessageCircle, Heart, Eye, Settings, Plus, Filter, Search } from 'lucide-react'
import { GuruProfile, GuruConnectApi } from '../../services/guruConnectApi'

interface GroupSession {
  id: string
  title: string
  description: string
  guru: GuruProfile
  session_type: 'group' | 'live_stream' | 'workshop' | 'webinar'
  max_participants: number
  current_participants: number
  price: number
  duration_minutes: number
  scheduled_at: string
  status: 'upcoming' | 'live' | 'completed' | 'cancelled'
  tags: string[]
  thumbnail_url?: string
  recording_url?: string
  is_featured: boolean
  category: string
}

interface LiveStreamState {
  isLive: boolean
  viewers: number
  likes: number
  comments: number
  duration: number
  quality: 'low' | 'medium' | 'high'
  isRecording: boolean
}

interface GroupSessionsProps {
  userRole: 'client' | 'guru' | 'admin'
  currentUserId?: string
}

export const GroupSessions: React.FC<GroupSessionsProps> = ({ userRole, currentUserId }) => {
  const [sessions, setSessions] = useState<GroupSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedSession, setSelectedSession] = useState<GroupSession | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [liveStreamState, setLiveStreamState] = useState<LiveStreamState>({
    isLive: false,
    viewers: 0,
    likes: 0,
    comments: 0,
    duration: 0,
    quality: 'medium',
    isRecording: false
  })
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [filterCategory, setFilterCategory] = useState<string>('all')

  useEffect(() => {
    loadSessions()
  }, [])

  const loadSessions = async () => {
    try {
      setLoading(true)
      setError('')
      
      // In a real app, this would fetch group sessions from the backend
      const mockSessions: GroupSession[] = [
        {
          id: '1',
          title: 'Diabetes Management Masterclass',
          description: 'Comprehensive guide to managing diabetes through lifestyle changes, nutrition, and medication.',
          guru: {
            id: 'guru1',
            user_id: 'user1',
            guru_role: 'nutritionist',
            specialties: ['diabetes', 'nutrition', 'weight_loss'],
            bio: 'Certified nutritionist with 10+ years experience in diabetes management.',
            session_rate: 150,
            years_experience: 10,
            certifications: ['CDE', 'RD'],
            average_rating: 4.8,
            total_reviews: 156,
            total_sessions: 89,
            trust_score: 95,
            is_verified: true,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          session_type: 'live_stream',
          max_participants: 100,
          current_participants: 67,
          price: 25,
          duration_minutes: 90,
          scheduled_at: '2024-01-20T14:00:00Z',
          status: 'upcoming',
          tags: ['diabetes', 'nutrition', 'health'],
          thumbnail_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400',
          is_featured: true,
          category: 'health'
        },
        {
          id: '2',
          title: 'Stress Reduction Workshop',
          description: 'Learn practical techniques for managing stress and improving mental well-being.',
          guru: {
            id: 'guru2',
            user_id: 'user2',
            guru_role: 'therapist',
            specialties: ['mental_health', 'stress_management', 'mindfulness'],
            bio: 'Licensed therapist specializing in stress management and mindfulness.',
            session_rate: 120,
            years_experience: 8,
            certifications: ['LCSW', 'MBSR'],
            average_rating: 4.9,
            total_reviews: 203,
            total_sessions: 145,
            trust_score: 98,
            is_verified: true,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          session_type: 'workshop',
          max_participants: 20,
          current_participants: 18,
          price: 45,
          duration_minutes: 120,
          scheduled_at: '2024-01-22T10:00:00Z',
          status: 'upcoming',
          tags: ['stress', 'mental_health', 'workshop'],
          thumbnail_url: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400',
          is_featured: false,
          category: 'wellness'
        },
        {
          id: '3',
          title: 'Fitness Fundamentals Live',
          description: 'Live fitness session covering basic exercises and workout routines.',
          guru: {
            id: 'guru3',
            user_id: 'user3',
            guru_role: 'fitness_trainer',
            specialties: ['fitness', 'strength_training', 'cardio'],
            bio: 'Certified personal trainer with expertise in strength training and cardio.',
            session_rate: 100,
            years_experience: 6,
            certifications: ['NASM', 'ACE'],
            average_rating: 4.7,
            total_reviews: 89,
            total_sessions: 67,
            trust_score: 92,
            is_verified: true,
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          session_type: 'live_stream',
          max_participants: 50,
          current_participants: 23,
          price: 15,
          duration_minutes: 60,
          scheduled_at: '2024-01-19T16:00:00Z',
          status: 'live',
          tags: ['fitness', 'workout', 'exercise'],
          thumbnail_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400',
          is_featured: true,
          category: 'fitness'
        }
      ]

      setSessions(mockSessions)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sessions')
    } finally {
      setLoading(false)
    }
  }

  const joinSession = async (sessionId: string) => {
    try {
      // In a real app, this would handle session joining logic
      console.log('Joining session:', sessionId)
      alert('Successfully joined the session!')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join session')
    }
  }

  const startLiveStream = async (sessionId: string) => {
    try {
      setLiveStreamState(prev => ({ ...prev, isLive: true }))
      // In a real app, this would start the live stream
      console.log('Starting live stream for session:', sessionId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start live stream')
    }
  }

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = filterType === 'all' || session.session_type === filterType
    const matchesStatus = filterStatus === 'all' || session.status === filterStatus
    const matchesCategory = filterCategory === 'all' || session.category === filterCategory
    
    return matchesSearch && matchesType && matchesStatus && matchesCategory
  })

  const getSessionTypeIcon = (type: string) => {
    switch (type) {
      case 'live_stream':
        return <Video className="w-5 h-5" />
      case 'group':
        return <Users className="w-5 h-5" />
      case 'workshop':
        return <Settings className="w-5 h-5" />
      case 'webinar':
        return <Video className="w-5 h-5" />
      default:
        return <Users className="w-5 h-5" />
    }
  }

  const getSessionTypeColor = (type: string) => {
    switch (type) {
      case 'live_stream':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'group':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'workshop':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'webinar':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'upcoming':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading group sessions...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Group Sessions & Live Streams</h2>
          <p className="text-gray-600 dark:text-gray-300">
            Join interactive group sessions and live streams with health professionals
          </p>
        </div>
        
        {userRole === 'guru' && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Session</span>
          </button>
        )}
      </div>

      {/* Live Stream Controls (if currently streaming) */}
      {liveStreamState.isLive && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-red-800 dark:text-red-200">LIVE</span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-red-700 dark:text-red-300">
                <div className="flex items-center space-x-1">
                  <Eye className="w-4 h-4" />
                  <span>{liveStreamState.viewers} viewers</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Heart className="w-4 h-4" />
                  <span>{liveStreamState.likes} likes</span>
                </div>
                <div className="flex items-center space-x-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{liveStreamState.comments} comments</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors">
                <Mic className="w-4 h-4" />
              </button>
              <button className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors">
                <VideoIcon className="w-4 h-4" />
              </button>
              <button className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-full transition-colors">
                <Share2 className="w-4 h-4" />
              </button>
              <button 
                onClick={() => setLiveStreamState(prev => ({ ...prev, isLive: false }))}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-md font-medium transition-colors"
              >
                End Stream
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Types</option>
            <option value="live_stream">Live Streams</option>
            <option value="group">Group Sessions</option>
            <option value="workshop">Workshops</option>
            <option value="webinar">Webinars</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="live">Live Now</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
          </select>
          
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Categories</option>
            <option value="health">Health</option>
            <option value="fitness">Fitness</option>
            <option value="wellness">Wellness</option>
            <option value="nutrition">Nutrition</option>
          </select>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Sessions Grid */}
      {filteredSessions.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Sessions Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search criteria or check back later for new sessions.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedSession(session)}
            >
              {/* Thumbnail */}
              <div className="relative h-48 bg-gradient-to-br from-blue-400 to-purple-500">
                {session.thumbnail_url ? (
                  <img
                    src={session.thumbnail_url}
                    alt={session.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    {getSessionTypeIcon(session.session_type)}
                  </div>
                )}
                
                <div className="absolute top-3 left-3 flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSessionTypeColor(session.session_type)}`}>
                    {session.session_type.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                    {session.status.toUpperCase()}
                  </span>
                </div>
                
                {session.is_featured && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 rounded-full text-xs font-medium">
                      FEATURED
                    </span>
                  </div>
                )}
                
                {session.status === 'live' && (
                  <div className="absolute bottom-3 left-3">
                    <div className="flex items-center space-x-2 bg-red-600 text-white px-3 py-1 rounded-full">
                      <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium">LIVE</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                  {session.title}
                </h3>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {session.description}
                </p>
                
                <div className="flex items-center space-x-4 mb-3">
                  <div className="flex items-center space-x-1">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {session.current_participants}/{session.max_participants}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {session.duration_minutes}m
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-600">
                      {GuruConnectApi.formatPrice(session.price)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <img
                      src={GuruConnectApi.getGuruAvatar(session.guru)}
                      alt={GuruConnectApi.getGuruDisplayName(session.guru)}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {GuruConnectApi.getGuruDisplayName(session.guru)}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-current" />
                    <span className="text-sm font-medium">{session.guru.average_rating}</span>
                  </div>
                </div>
                
                <div className="mt-3 flex space-x-2">
                  {session.status === 'live' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // Join live session
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      <Play className="w-4 h-4 inline mr-1" />
                      Join Live
                    </button>
                  ) : session.status === 'upcoming' ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        joinSession(session.id)
                      }}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      Join Session
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        // View recording
                      }}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      View Recording
                    </button>
                  )}
                  
                  {userRole === 'guru' && session.status === 'upcoming' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        startLiveStream(session.id)
                      }}
                      className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium transition-colors"
                    >
                      <Video className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedSession.title}
                </h2>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Session Info */}
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Description</h3>
                    <p className="text-gray-600 dark:text-gray-300">{selectedSession.description}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Type:</span>
                      <p className="font-medium">{selectedSession.session_type.replace('_', ' ').toUpperCase()}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Duration:</span>
                      <p className="font-medium">{selectedSession.duration_minutes} minutes</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Price:</span>
                      <p className="font-medium text-green-600">{GuruConnectApi.formatPrice(selectedSession.price)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Participants:</span>
                      <p className="font-medium">{selectedSession.current_participants}/{selectedSession.max_participants}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedSession.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-sm">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Guru Info */}
                <div className="space-y-6">
                  <div className="flex items-center space-x-4">
                    <img
                      src={GuruConnectApi.getGuruAvatar(selectedSession.guru)}
                      alt={GuruConnectApi.getGuruDisplayName(selectedSession.guru)}
                      className="w-16 h-16 rounded-full"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {GuruConnectApi.getGuruDisplayName(selectedSession.guru)}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400">
                        {GuruConnectApi.getGuruRoleDisplay(selectedSession.guru.guru_role)}
                      </p>
                      <div className="flex items-center space-x-2 mt-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="font-medium">{selectedSession.guru.average_rating}</span>
                        <span className="text-gray-500">({selectedSession.guru.total_reviews} reviews)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedSession.guru.specialties.map((specialty, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full text-sm">
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex space-x-3 pt-4">
                    {selectedSession.status === 'live' ? (
                      <button className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        <Play className="w-4 h-4 inline mr-2" />
                        Join Live Stream
                      </button>
                    ) : selectedSession.status === 'upcoming' ? (
                      <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        Join Session
                      </button>
                    ) : (
                      <button className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md font-medium transition-colors">
                        View Recording
                      </button>
                    )}
                    <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      Share
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 